import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  Search,
  Package,
  Hammer,
  Loader2,
  Calculator,
  AlertCircle,
  AlertTriangle,
  Camera,
  Upload,
  Edit,
  ShoppingCart,
  ChevronDown,
  ChevronRight,
  Link as LinkIcon,
  Scissors,
  User,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { Modal } from '@components/Dialog';
import { ProductProjectPhotoThumb } from '@components/ProductProjectPhotoThumb';
import { PermissionGuard } from '@components/PermissionGuard';
import { CustomerSearchModal } from '@components/CustomerSearchModal';
import { DateInputBR } from '@components/DateInputBR';
import { usePermissions } from '@hooks/usePermissions';
import { materialReceiptService } from '../../services/materialReceiptService';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { rawMaterialService } from '../../services/rawMaterialService';
import { providerService } from '../../services/providerService';
import {
  MaterialReceiptFormData,
  MaterialReceiptItemRow,
  MaterialReceiptInstallmentRow,
  PurchaseOrderForReceipt,
  PurchaseOrderItemForReceipt,
  MATERIAL_RECEIPT_STATUS,
  CustomerOrderForReceipt,
} from '../../types/materialReceipt';
import {
  PaymentMethod,
  ProviderProduct,
  PurchaseCreditCard,
} from '../../types/purchaseOrder';
import { isCreditCardMethodName } from '../purchaseOrders/paymentTermUtils';
import { RawMaterial, UNIT_OPTIONS } from '../../types/rawMaterial';
import { Provider } from '../../types/provider';
import { Customer } from '../../types/salesOrder';

// ─── Helpers ─────────────────────────────────────────────────────

const uid = () => Math.random().toString(36).slice(2, 10);

const formatCurrency = (v?: number) =>
  v !== undefined && v !== null
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : 'R$ 0,00';

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + (dateStr.length === 10 ? 'T00:00:00' : ''));
  return d.toLocaleDateString('pt-BR');
};

// ─── Search Modal (generic) ─────────────────────────────────────

interface SearchModalProps<T> {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  onSelect: (item: T) => void;
  searchFn: (q: string) => Promise<T[]>;
  renderItem: (item: T) => React.ReactNode;
  keyFn: (item: T) => string | number;
}

function SearchModal<T>({ isOpen, onClose, title, onSelect, searchFn, renderItem, keyFn }: SearchModalProps<T>) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) { setQuery(''); setResults([]); return; }
  }, [isOpen]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const items = await searchFn(query);
        setResults(items ?? []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">{title}</h3>
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            autoFocus
            className="input pl-10 w-full"
            placeholder="Digite para buscar..."
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
        <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg divide-y">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-emerald-600" size={24} />
            </div>
          )}
          {!loading && (results ?? []).length === 0 && query.trim() && (
            <div className="text-center py-8 text-slate-400 text-sm">Nenhum resultado encontrado.</div>
          )}
          {!loading && (results ?? []).length === 0 && !query.trim() && (
            <div className="text-center py-8 text-slate-400 text-sm">Digite para pesquisar.</div>
          )}
          {!loading && (results ?? []).map(item => (
            <button
              key={keyFn(item)}
              className="w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors"
              onClick={() => { onSelect(item); onClose(); }}
            >
              {renderItem(item)}
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}

// ─── Purchase Order Selection Modal ──────────────────────────────

interface PurchaseOrderSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  providerId: number;
  onAddItems: (items: MaterialReceiptItemRow[]) => void;
}

const PurchaseOrderSelectionModal: React.FC<PurchaseOrderSelectionModalProps> = ({
  isOpen,
  onClose,
  providerId,
  onAddItems,
}) => {
  const [loading, setLoading] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrderForReceipt[]>([]);
  const [expandedPOs, setExpandedPOs] = useState<Set<number>>(new Set());
  const [selectedItems, setSelectedItems] = useState<Map<string, { poItem: PurchaseOrderItemForReceipt; poId: number; poOrderNumber?: number }>>(new Map());
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!isOpen || !providerId) return;
    (async () => {
      setLoading(true);
      try {
        const pos = await materialReceiptService.getPurchaseOrdersByProvider(providerId);
        setPurchaseOrders(pos);
        // expand all by default
        setExpandedPOs(new Set(pos.map(po => po.purchase_order_id)));
      } catch { setPurchaseOrders([]); }
      finally { setLoading(false); }
    })();
    return () => { setSelectedItems(new Map()); setExpandedPOs(new Set()); setSearchQuery(''); };
  }, [isOpen, providerId]);

  const togglePO = (poId: number) => {
    setExpandedPOs(prev => {
      const next = new Set(prev);
      if (next.has(poId)) next.delete(poId);
      else next.add(poId);
      return next;
    });
  };

  const itemKey = (poId: number, itemId: number) => `${poId}-${itemId}`;

  const isItemSelected = (poId: number, itemId: number) => selectedItems.has(itemKey(poId, itemId));

  const toggleItem = (poId: number, poItem: PurchaseOrderItemForReceipt, poOrderNumber?: number) => {
    setSelectedItems(prev => {
      const next = new Map(prev);
      const key = itemKey(poId, poItem.purchase_order_item_id);
      if (next.has(key)) next.delete(key);
      else next.set(key, { poItem, poId, poOrderNumber });
      return next;
    });
  };

  const toggleAllPOItems = (po: PurchaseOrderForReceipt) => {
    const allSelected = po.items.every(i => isItemSelected(po.purchase_order_id, i.purchase_order_item_id));
    setSelectedItems(prev => {
      const next = new Map(prev);
      if (allSelected) {
        po.items.forEach(i => next.delete(itemKey(po.purchase_order_id, i.purchase_order_item_id)));
      } else {
        po.items.forEach(i => next.set(itemKey(po.purchase_order_id, i.purchase_order_item_id), { poItem: i, poId: po.purchase_order_id, poOrderNumber: po.order_number }));
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const newItems: MaterialReceiptItemRow[] = [];
    selectedItems.forEach(({ poItem, poId, poOrderNumber }) => {
      newItems.push({
        _tempId: uid(),
        _type: poItem.raw_material_id ? 'raw_material' : 'product',
        _sourceCode: poItem.sku || '',
        description: poItem.description,
        unit: poItem.unit || 'UN',
        unit_value: poItem.unit_value,
        quantity: poItem.quantity,
        product_id: poItem.product_id,
        raw_material_id: poItem.raw_material_id,
        purchase_order_id: poId,
        purchase_order_number: poOrderNumber,
        is_custom_product: poItem.is_custom_product === true,
      });
    });
    onAddItems(newItems);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Adicionar de Pedido de Compra</h3>
        <p className="text-sm text-slate-500 mb-3">
          Selecione os itens dos pedidos de compra (Em Aberto / Em Andamento) para adicionar ao recebimento.
        </p>
        <div className="relative mb-4">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Pesquisar por SKU ou descrição..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-emerald-600" size={32} />
          </div>
        ) : purchaseOrders.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <ShoppingCart size={40} className="mx-auto mb-2" />
            <p>Nenhum pedido de compra em aberto ou em andamento encontrado para este fornecedor.</p>
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto border border-slate-200 rounded-lg">
            {purchaseOrders.map(po => {
              const q = searchQuery.trim().toLowerCase();
              const filteredItems = q
                ? po.items.filter(i =>
                    (i.sku || '').toLowerCase().includes(q) ||
                    (i.description || '').toLowerCase().includes(q)
                  )
                : po.items;
              if (filteredItems.length === 0) return null;
              const isExpanded = expandedPOs.has(po.purchase_order_id);
              const allSelected = filteredItems.every(i => isItemSelected(po.purchase_order_id, i.purchase_order_item_id));
              const someSelected = filteredItems.some(i => isItemSelected(po.purchase_order_id, i.purchase_order_item_id));

              return (
                <div key={po.purchase_order_id} className="border-b border-slate-200 last:border-b-0">
                  {/* PO header row */}
                  <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={input => { if (input) input.indeterminate = someSelected && !allSelected; }}
                      onChange={() => toggleAllPOItems(po)}
                      className="rounded border-slate-300"
                    />
                    <button className="flex items-center gap-2 flex-1 text-left" onClick={() => togglePO(po.purchase_order_id)}>
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      <ShoppingCart size={16} className="text-emerald-600" />
                      <span className="font-medium text-slate-900">
                        Pedido #{po.order_number || po.purchase_order_id}
                      </span>
                      {po.is_priority && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700">PRIORITÁRIO</span>
                      )}
                      <span className="text-xs text-slate-500 ml-2">
                        {formatDate(po.order_date)} • {po.items.length} item(ns) • {formatCurrency(po.total)}
                        {(po.sales_orders?.length ?? 0) > 0 && (
                          <span className="text-emerald-600"> • → {po.sales_orders!.map(l => `PV #${l.order_id}${l.customer_name ? ` (${l.customer_name})` : ''}`).join(', ')}</span>
                        )}
                      </span>
                      <span className={`ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${po.status === 0 ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                        {po.status === 0 ? 'Em Aberto' : 'Em Andamento'}
                      </span>
                    </button>
                  </div>

                  {/* PO items (spreadsheet style) */}
                  {isExpanded && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-white border-b border-slate-100 text-xs text-slate-500 uppercase">
                            <th className="py-1.5 px-4 w-10"></th>
                            <th className="py-1.5 px-2">Tipo</th>
                            <th className="py-1.5 px-2">SKU</th>
                            <th className="py-1.5 px-2">Descrição</th>
                            <th className="py-1.5 px-2">Unid.</th>
                            <th className="py-1.5 px-2 text-right">Valor Unit.</th>
                            <th className="py-1.5 px-2 text-right">Qtde</th>
                            <th className="py-1.5 px-2 text-right">Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredItems.map(item => {
                            const isRaw = !!item.raw_material_id;
                            const selected = isItemSelected(po.purchase_order_id, item.purchase_order_item_id);
                            return (
                              <tr
                                key={item.purchase_order_item_id}
                                className={`border-b border-slate-50 hover:bg-emerald-50/50 cursor-pointer ${selected ? 'bg-emerald-50' : ''}`}
                                onClick={() => toggleItem(po.purchase_order_id, item, po.order_number)}
                              >
                                <td className="py-1.5 px-4">
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={() => toggleItem(po.purchase_order_id, item, po.order_number)}
                                    className="rounded border-slate-300"
                                    onClick={e => e.stopPropagation()}
                                  />
                                </td>
                                <td className="py-1.5 px-2">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${isRaw ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                                    {isRaw ? <Hammer size={10} /> : <Package size={10} />}
                                    {isRaw ? 'MP' : 'Prod'}
                                  </span>
                                </td>
                                <td className="py-1.5 px-2 text-slate-600 text-xs font-mono">{item.sku || '—'}</td>
                                <td className="py-1.5 px-2 text-slate-900">{item.description}</td>
                                <td className="py-1.5 px-2 text-xs">{item.unit || '—'}</td>
                                <td className="py-1.5 px-2 text-right text-xs">{formatCurrency(item.unit_value)}</td>
                                <td className="py-1.5 px-2 text-right text-xs">{item.quantity}</td>
                                <td className="py-1.5 px-2 text-right text-xs font-medium">{formatCurrency(item.unit_value * item.quantity)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-slate-500">{selectedItems.size} item(ns) selecionado(s)</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={handleConfirm} disabled={selectedItems.size === 0}>
              Adicionar Selecionados
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

// ─── Customer Orders Selection Modal ─────────────────────────────

interface CustomerOrdersSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  orders: CustomerOrderForReceipt[];
  expandedOrderId: number | null;
  onToggleOrder: (orderId: number | null) => void;
  onAddItems: (items: { sku: string; description?: string; quantity: number; unit_price?: number; product_id?: number; orderId: number }[]) => void;
}

const CustomerOrdersSelectionModal: React.FC<CustomerOrdersSelectionModalProps> = ({
  isOpen,
  onClose,
  orders,
  expandedOrderId,
  onToggleOrder,
  onAddItems,
}) => {
  // Selected items: key = `${orderId}-${sku}`, value = { ...item data, quantity to add }
  const [selectedItems, setSelectedItems] = useState<Map<string, { sku: string; description?: string; quantity: number; maxQuantity: number; unit_price?: number; product_id?: number; orderId: number; isReplacement: boolean }>>(new Map());

  useEffect(() => {
    if (!isOpen) {
      setSelectedItems(new Map());
      onToggleOrder(null);
    }
  }, [isOpen]);

  const itemKey = (orderId: number, sku: string, isReplacement: boolean) => `${orderId}-${sku}-${isReplacement ? 'r' : 'n'}`;

  const toggleItem = (orderId: number, sku: string, description: string | undefined, maxQuantity: number, unit_price: number | undefined, product_id: number | undefined, isReplacement: boolean, originalOrderId?: number) => {
    const key = itemKey(orderId, sku, isReplacement);
    setSelectedItems(prev => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        // For replacement items, orderId reference should be the original order
        const effectiveOrderId = isReplacement && originalOrderId ? originalOrderId : orderId;
        next.set(key, { sku, description, quantity: maxQuantity, maxQuantity, unit_price, product_id, orderId: effectiveOrderId, isReplacement });
      }
      return next;
    });
  };

  const updateQuantity = (key: string, qty: number) => {
    setSelectedItems(prev => {
      const next = new Map(prev);
      const item = next.get(key);
      if (item) {
        next.set(key, { ...item, quantity: Math.min(Math.max(1, qty), item.maxQuantity) });
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const items = Array.from(selectedItems.values()).map(item => ({
      sku: item.sku,
      description: item.description,
      quantity: item.quantity,
      unit_price: item.unit_price,
      product_id: item.product_id,
      orderId: item.orderId,
    }));
    onAddItems(items);
  };

  const formatCurrencyLocal = (v?: number) =>
    v !== undefined && v !== null
      ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : '—';

  const formatDateLocal = (dateStr?: string) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr + (dateStr.length === 10 ? 'T00:00:00' : ''));
    return d.toLocaleDateString('pt-BR');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Pedidos de Venda do Cliente</h3>
        <p className="text-sm text-slate-500 mb-4">
          Expanda os pedidos e selecione os itens que deseja receber. Para itens de devolução, a referência ao pedido original será preservada.
        </p>

        {orders.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <ShoppingCart size={40} className="mx-auto mb-2" />
            <p>Nenhum pedido de venda encontrado para este cliente.</p>
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto border border-slate-200 rounded-lg">
            {orders.map(order => {
              const isExpanded = expandedOrderId === order.order_id;
              return (
                <div key={order.order_id} className="border-b border-slate-200 last:border-b-0">
                  {/* Order header */}
                  <button
                    className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 text-left"
                    onClick={() => onToggleOrder(isExpanded ? null : order.order_id)}
                  >
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <ShoppingCart size={16} className="text-emerald-600" />
                    <span className="font-medium text-slate-900">Pedido #{order.order_id}</span>
                    <span className="text-xs text-slate-500 ml-2">
                      {formatDateLocal(order.order_date)} • {formatCurrencyLocal(order.order_sell_total)}
                    </span>
                    {order.order_status && (
                      <span className="ml-auto text-xs text-slate-500">{order.order_status}</span>
                    )}
                  </button>

                  {/* Order items */}
                  {isExpanded && (
                    <div className="px-4 pb-4">
                      {/* Normal items */}
                      {order.items && order.items.length > 0 && (
                        <div className="mt-3">
                          <h4 className="text-xs font-semibold text-slate-600 uppercase mb-2 flex items-center gap-1">
                            <Package size={12} /> Itens do Pedido
                          </h4>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase">
                                <th className="py-1.5 px-2 w-10"></th>
                                <th className="py-1.5 px-2">SKU</th>
                                <th className="py-1.5 px-2">Descrição</th>
                                <th className="py-1.5 px-2 text-right">Valor Unit.</th>
                                <th className="py-1.5 px-2 text-right">Qtde Pedido</th>
                                <th className="py-1.5 px-2 text-right">Qtde Receber</th>
                              </tr>
                            </thead>
                            <tbody>
                              {order.items.map(item => {
                                const key = itemKey(order.order_id, item.sku, false);
                                const selected = selectedItems.has(key);
                                return (
                                  <tr key={item.order_item_id} className={`border-b border-slate-50 hover:bg-emerald-50/50 ${selected ? 'bg-emerald-50' : ''}`}>
                                    <td className="py-1.5 px-2">
                                      <input
                                        type="checkbox"
                                        checked={selected}
                                        onChange={() => toggleItem(order.order_id, item.sku, item.description, item.quantity, item.unit_price, item.product_id, false)}
                                        className="rounded border-slate-300"
                                      />
                                    </td>
                                    <td className="py-1.5 px-2 text-xs font-mono text-slate-600">{item.sku}</td>
                                    <td className="py-1.5 px-2 text-slate-900">{item.description || '—'}</td>
                                    <td className="py-1.5 px-2 text-right text-xs">{formatCurrencyLocal(item.unit_price)}</td>
                                    <td className="py-1.5 px-2 text-right text-xs">{item.quantity}</td>
                                    <td className="py-1.5 px-2 text-right">
                                      {selected ? (
                                        <input
                                          type="number"
                                          min={1}
                                          max={item.quantity}
                                          className="w-16 input text-xs text-right"
                                          value={selectedItems.get(key)?.quantity ?? item.quantity}
                                          onChange={e => updateQuantity(key, parseInt(e.target.value) || 1)}
                                          onClick={e => e.stopPropagation()}
                                        />
                                      ) : (
                                        <span className="text-xs text-slate-400">—</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Replacement items (devoluções) */}
                      {order.replacement_items && order.replacement_items.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-xs font-semibold text-orange-600 uppercase mb-2 flex items-center gap-1">
                            <RotateCcw size={12} /> Itens de Devolução
                          </h4>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-orange-100 text-xs text-slate-500 uppercase">
                                <th className="py-1.5 px-2 w-10"></th>
                                <th className="py-1.5 px-2">SKU</th>
                                <th className="py-1.5 px-2">Descrição</th>
                                <th className="py-1.5 px-2 text-right">Pedido Original</th>
                                <th className="py-1.5 px-2 text-right">Qtde</th>
                                <th className="py-1.5 px-2 text-right">Qtde Receber</th>
                              </tr>
                            </thead>
                            <tbody>
                              {order.replacement_items.map(item => {
                                const key = itemKey(order.order_id, item.sku, true);
                                const selected = selectedItems.has(key);
                                return (
                                  <tr key={item.replacement_order_item_id} className={`border-b border-orange-50 hover:bg-orange-50/50 ${selected ? 'bg-orange-50' : ''}`}>
                                    <td className="py-1.5 px-2">
                                      <input
                                        type="checkbox"
                                        checked={selected}
                                        onChange={() => toggleItem(order.order_id, item.sku, item.description, item.quantity, undefined, item.product_id, true, item.original_order_id)}
                                        className="rounded border-orange-300"
                                      />
                                    </td>
                                    <td className="py-1.5 px-2 text-xs font-mono text-slate-600">{item.sku}</td>
                                    <td className="py-1.5 px-2 text-slate-900">{item.description || '—'}</td>
                                    <td className="py-1.5 px-2 text-right text-xs text-orange-600">#{item.original_order_id}</td>
                                    <td className="py-1.5 px-2 text-right text-xs">{item.quantity}</td>
                                    <td className="py-1.5 px-2 text-right">
                                      {selected ? (
                                        <input
                                          type="number"
                                          min={1}
                                          max={item.quantity}
                                          className="w-16 input text-xs text-right"
                                          value={selectedItems.get(key)?.quantity ?? item.quantity}
                                          onChange={e => updateQuantity(key, parseInt(e.target.value) || 1)}
                                          onClick={e => e.stopPropagation()}
                                        />
                                      ) : (
                                        <span className="text-xs text-slate-400">—</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-slate-500">{selectedItems.size} item(ns) selecionado(s)</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={handleConfirm} disabled={selectedItems.size === 0}>
              Adicionar Selecionados
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

// ─── Camera Capture Modal ────────────────────────────────────────

interface CameraCaptureProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
}

const CameraCapture: React.FC<CameraCaptureProps> = ({ isOpen, onClose, onCapture }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [camError, setCamError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setStream(null);
      setCamError(null);
      return;
    }
    let active: MediaStream | null = null;
    const startCamera = async () => {
      try {
        try {
          active = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        } catch {
          active = await navigator.mediaDevices.getUserMedia({ video: true });
        }
        setStream(active);
        if (videoRef.current) videoRef.current.srcObject = active;
      } catch {
        setCamError('Câmera não disponível ou permissão negada.');
      }
    };
    startCamera();
    return () => { active?.getTracks().forEach(t => t.stop()); };
  }, [isOpen]);

  const handleCapture = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d')?.drawImage(videoRef.current, 0, 0);
    canvas.toBlob(blob => {
      if (!blob) return;
      const file = new File([blob], `avaria_${Date.now()}.jpg`, { type: 'image/jpeg' });
      stream?.getTracks().forEach(t => t.stop());
      onCapture(file);
      onClose();
    }, 'image/jpeg', 0.92);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={() => { stream?.getTracks().forEach(t => t.stop()); onClose(); }} size="lg">
      <div className="p-4">
        <h3 className="text-base font-semibold text-slate-900 mb-3">Câmera</h3>
        {camError ? (
          <div className="text-center py-6">
            <p className="text-red-600 text-sm mb-4">{camError}</p>
            <Button variant="outline" onClick={onClose}>Fechar</Button>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full rounded-lg bg-black"
              style={{ maxHeight: '340px', objectFit: 'cover' }}
            />
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" onClick={() => { stream?.getTracks().forEach(t => t.stop()); onClose(); }}>
                Cancelar
              </Button>
              <Button onClick={handleCapture} icon={<Camera size={16} />}>
                Capturar
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

// ─── Damage Report Modal ─────────────────────────────────────────

interface DamageModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: MaterialReceiptItemRow | null;
  onConfirm: (damageNotes: string, damageQuantity: number, damagePhotos: File[]) => void;
  /** Modo de edição: pré-popula notas e exibe fotos já salvas */
  mode?: 'create' | 'edit';
  initialNotes?: string;
  initialQuantity?: number;
  /** ID do recebimento — necessário em modo 'edit' para gerenciar fotos via API */
  receiptId?: number;
  /** ID do item de avaria persistido — necessário em modo 'edit' */
  existingItemId?: number;
}

/** Comprime imagem antes do upload (mesmo padrão da tela de entrega do motorista) */
const compressImage = async (file: File): Promise<File> => {
  try {
    const imageCompression = (await import('browser-image-compression')).default;
    return await imageCompression(file, {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 1280,
      useWebWorker: true,
      fileType: 'image/jpeg',
    });
  } catch {
    return file;
  }
};

const DamageModal: React.FC<DamageModalProps> = ({
  isOpen, onClose, item, onConfirm,
  mode = 'create', initialNotes = '', initialQuantity = 1, receiptId, existingItemId,
}) => {
  const [damageNotes, setDamageNotes] = useState('');
  const [damageQuantity, setDamageQuantity] = useState<number | ''>('');  // inicia vazio em modo create
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const [newPhotos, setNewPhotos] = useState<{ file: File; preview: string }[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<{ id: number; url: string }[]>([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [deletingPhotoId, setDeletingPhotoId] = useState<number | null>(null);
  const [notesError, setNotesError] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setDamageNotes(initialNotes);
      // Em modo create, deixa o campo vazio para o usuário digitar; em edit, pré-preenche
      setDamageQuantity(mode === 'edit' ? initialQuantity : '');
      setNewPhotos([]);
      setExistingPhotos([]);
      setNotesError('');
      setCameraOpen(false);
      // Foca o campo de quantidade após a abertura do modal
      setTimeout(() => quantityInputRef.current?.focus(), 100);

      // Em modo edit, carrega fotos existentes via proxy
      if (mode === 'edit' && receiptId && existingItemId) {
        setPhotosLoading(true);
        // Tenta o novo endpoint de múltiplas fotos; se não existir, tenta o endpoint legado
        materialReceiptService.getDamagePhotos(receiptId, existingItemId)
          .then(async (photos) => {
            const loaded: { id: number; url: string }[] = [];
            for (const p of photos) {
              if (p.material_receipt_item_damage_photo_id) {
                try {
                  const url = await materialReceiptService.getDamagePhotoFile(
                    receiptId, existingItemId, p.material_receipt_item_damage_photo_id,
                  );
                  loaded.push({ id: p.material_receipt_item_damage_photo_id, url });
                } catch { /* ignora fotos que falharam */ }
              }
            }
            setExistingPhotos(loaded);
          })
          .catch(async () => {
            // Fallback: endpoint legado de foto única
            try {
              const url = await materialReceiptService.getDamagePhoto(receiptId, existingItemId);
              if (url) setExistingPhotos([{ id: -1, url }]);
            } catch { /* sem fotos */ }
          })
          .finally(() => setPhotosLoading(false));
      }
    } else {
      setDamageNotes('');
      setDamageQuantity('');
      // Revoga URLs de preview
      newPhotos.forEach(p => URL.revokeObjectURL(p.preview));
      existingPhotos.forEach(p => URL.revokeObjectURL(p.url));
      setNewPhotos([]);
      setExistingPhotos([]);
      setNotesError('');
      setCameraOpen(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const addPhotoFile = async (file: File) => {
    const compressed = await compressImage(file);
    const preview = URL.createObjectURL(compressed);
    setNewPhotos(prev => [...prev, { file: compressed, preview }]);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (let i = 0; i < files.length; i++) {
      await addPhotoFile(files[i]);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeNewPhoto = (idx: number) => {
    setNewPhotos(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[idx].preview);
      updated.splice(idx, 1);
      return updated;
    });
  };

  const handleDeleteExistingPhoto = async (photoId: number) => {
    if (!receiptId || !existingItemId) return;
    try {
      setDeletingPhotoId(photoId);
      // photoId = -1 indica foto carregada via endpoint legado (não pode ser deletada via API de fotos)
      if (photoId > 0) {
        await materialReceiptService.deleteDamagePhoto(receiptId, existingItemId, photoId);
      }
      setExistingPhotos(prev => {
        const photo = prev.find(p => p.id === photoId);
        if (photo) URL.revokeObjectURL(photo.url);
        return prev.filter(p => p.id !== photoId);
      });
    } catch (err) {
      console.error('Erro ao remover foto:', err);
    } finally {
      setDeletingPhotoId(null);
    }
  };

  const handleSubmit = () => {
    if (!damageNotes.trim()) {
      setNotesError('A descrição da avaria é obrigatória');
      return;
    }
    const qty = typeof damageQuantity === 'number' ? damageQuantity : parseFloat(String(damageQuantity));
    if (!qty || qty < 1) return;
    setNotesError('');
    onConfirm(damageNotes.trim(), qty, newPhotos.map(p => p.file));
    onClose();
  };

  if (!item) return null;

  const maxQuantity = mode === 'create' ? item.quantity : item.quantity;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle size={24} className="text-red-500" />
          <h3 className="text-lg font-semibold text-slate-900">
            {mode === 'edit' ? 'Editar Avaria' : 'Registrar Avaria'}
          </h3>
        </div>

        <div className="space-y-4">
          {/* Informação do item */}
          <div className={`p-3 ${mode === 'edit' ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'} border rounded-lg text-sm`}>
            <span className="font-medium text-slate-900">{item.description}</span>
            <span className="block text-xs text-slate-500 mt-0.5">
              Código: {item._sourceCode || '—'} • Qtde disponível: {item.quantity}
            </span>
          </div>

          {/* Quantidade de itens com avaria */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Quantidade com Avaria <span className="text-red-500">*</span>
            </label>
            <Input
              ref={quantityInputRef}
              type="number"
              inputMode="decimal"
              min={1}
              max={maxQuantity}
              step="0.01"
              placeholder="0"
              value={damageQuantity}
              onChange={e => {
                const v = e.target.value;
                setDamageQuantity(v === '' ? '' : parseFloat(v) || '');
              }}
            />
            {mode === 'create' && typeof damageQuantity === 'number' && damageQuantity > 0 && (
              <p className="text-xs text-slate-500 mt-1">
                Item original ficará com <span className="font-medium">{(item.quantity - damageQuantity).toFixed(2)}</span> unidades sem avaria.
              </p>
            )}
          </div>

          {/* Descrição da avaria (obrigatória) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Descrição da Avaria <span className="text-red-500">*</span>
            </label>
            <textarea
              className={`input w-full min-h-[80px] resize-y ${notesError ? 'border-red-400' : ''}`}
              placeholder="Descreva detalhadamente a avaria encontrada..."
              value={damageNotes}
              onChange={e => { setDamageNotes(e.target.value); if (e.target.value.trim()) setNotesError(''); }}
              rows={3}
            />
            {notesError && <p className="text-xs text-red-500 mt-1">{notesError}</p>}
          </div>

          {/* Fotos da avaria (múltiplas) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Fotos da Avaria</label>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                icon={<Upload size={16} />}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon={<Camera size={16} />}
                onClick={() => setCameraOpen(true)}
                className="hidden md:inline-flex"
              >
                Câmera
              </Button>
              {/* Botão mobile: abre câmera nativa diretamente via input[capture] */}
              <label className="md:hidden inline-flex items-center gap-1.5 cursor-pointer rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                <Camera size={16} />
                Câmera
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            {/* Fotos existentes (modo edição) */}
            {photosLoading && (
              <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
                <Loader2 size={16} className="animate-spin" /> Carregando fotos...
              </div>
            )}
            {existingPhotos.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-slate-500 mb-2">Fotos salvas:</p>
                <div className="flex flex-wrap gap-2">
                  {existingPhotos.map(photo => (
                    <div key={photo.id} className="relative inline-block">
                      <img src={photo.url} alt="Foto avaria" className="w-24 h-24 object-cover rounded-lg border" />
                      <button
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs disabled:opacity-50"
                        onClick={() => handleDeleteExistingPhoto(photo.id)}
                        disabled={deletingPhotoId === photo.id}
                        title="Remover foto"
                      >
                        {deletingPhotoId === photo.id ? '…' : '×'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Novas fotos (capturadas/upload) */}
            {newPhotos.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-slate-500 mb-2">Novas fotos ({newPhotos.length}):</p>
                <div className="flex flex-wrap gap-2">
                  {newPhotos.map((photo, idx) => (
                    <div key={idx} className="relative inline-block">
                      <img src={photo.preview} alt={`Preview ${idx + 1}`} className="w-24 h-24 object-cover rounded-lg border" />
                      <button
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                        onClick={() => removeNewPhoto(idx)}
                        title="Remover foto"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            disabled={!damageNotes.trim() || !damageQuantity || Number(damageQuantity) < 1 || (mode === 'create' && Number(damageQuantity) > item.quantity)}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {mode === 'edit' ? 'Salvar Alterações' : 'Registrar Avaria'}
          </Button>
        </div>
      </div>
      <CameraCapture
        isOpen={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={async file => {
          await addPhotoFile(file);
        }}
      />
    </Modal>
  );
};

// ─── Received Photos Modal (produto personalizado) ───────────────
// Fotos do que FOI RECEBIDO pela logística, para vendas/compras conferirem
// contra a Foto do Projeto aprovada. Mesmo padrão de fotos do DamageModal:
// upload múltiplo + câmera desktop/mobile + compressão; fotos novas ficam
// pendentes no item (_receivedPhotoFiles) e sobem depois do save.

interface ReceivedPhotosModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: MaterialReceiptItemRow | null;
  /** ID do recebimento — em edição permite gerenciar fotos já salvas */
  receiptId?: number;
  onConfirm: (photos: File[]) => void;
}

const ReceivedPhotosModal: React.FC<ReceivedPhotosModalProps> = ({ isOpen, onClose, item, receiptId, onConfirm }) => {
  const [newPhotos, setNewPhotos] = useState<{ file: File; preview: string }[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<{ id: number; url: string }[]>([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [deletingPhotoId, setDeletingPhotoId] = useState<number | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen || !item) {
      setNewPhotos(prev => { prev.forEach(p => URL.revokeObjectURL(p.preview)); return []; });
      setExistingPhotos(prev => { prev.forEach(p => URL.revokeObjectURL(p.url)); return []; });
      return;
    }
    // Fotos pendentes já anexadas ao item no formulário
    setNewPhotos((item._receivedPhotoFiles ?? []).map(file => ({ file, preview: URL.createObjectURL(file) })));

    // Fotos já salvas (item persistido em edição)
    const itemId = item.material_receipt_item_id;
    if (receiptId && itemId) {
      setPhotosLoading(true);
      (async () => {
        try {
          const photos = await materialReceiptService.getReceivedPhotos(receiptId, itemId);
          const loaded: { id: number; url: string }[] = [];
          for (const photo of photos) {
            if (!photo.material_receipt_item_received_photo_id) continue;
            try {
              const url = await materialReceiptService.getReceivedPhotoFile(receiptId, itemId, photo.material_receipt_item_received_photo_id);
              loaded.push({ id: photo.material_receipt_item_received_photo_id, url });
            } catch { /* foto indisponível */ }
          }
          setExistingPhotos(loaded);
        } catch { /* lista indisponível */ }
        finally { setPhotosLoading(false); }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, item?._tempId]);

  const addPhotoFile = async (file: File) => {
    const compressed = await compressImage(file);
    setNewPhotos(prev => [...prev, { file: compressed, preview: URL.createObjectURL(compressed) }]);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    for (const file of files) await addPhotoFile(file);
    e.target.value = '';
  };

  const removeNewPhoto = (idx: number) => {
    setNewPhotos(prev => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleDeleteExistingPhoto = async (photoId: number) => {
    if (!receiptId || !item?.material_receipt_item_id) return;
    setDeletingPhotoId(photoId);
    try {
      await materialReceiptService.deleteReceivedPhoto(receiptId, item.material_receipt_item_id, photoId);
      setExistingPhotos(prev => {
        const target = prev.find(p => p.id === photoId);
        if (target) URL.revokeObjectURL(target.url);
        return prev.filter(p => p.id !== photoId);
      });
    } catch { /* mantém a foto na lista */ }
    finally { setDeletingPhotoId(null); }
  };

  if (!item) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-1">Fotos do Produto Recebido</h3>
        <p className="text-sm text-slate-500 mb-4">
          {item.description} — produto personalizado. As fotos ficam disponíveis para vendas e
          compras conferirem o que foi entregue contra a Foto do Projeto aprovada.
        </p>

        {item.product_id && item.has_project_photo && (
          <div className="mb-4">
            <p className="text-xs text-slate-500 mb-1">Foto do projeto (referência):</p>
            <ProductProjectPhotoThumb productId={item.product_id} />
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" icon={<Upload size={16} />} onClick={() => fileInputRef.current?.click()}>
            Upload
          </Button>
          <Button variant="outline" size="sm" icon={<Camera size={16} />} onClick={() => setCameraOpen(true)} className="hidden md:inline-flex">
            Câmera
          </Button>
          <label className="md:hidden inline-flex items-center gap-1.5 cursor-pointer rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
            <Camera size={16} />
            Câmera
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileChange} />
          </label>
          <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />
        </div>

        {photosLoading && (
          <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 size={16} className="animate-spin" /> Carregando fotos...
          </div>
        )}
        {existingPhotos.length > 0 && (
          <div className="mt-3">
            <p className="text-xs text-slate-500 mb-2">Fotos salvas:</p>
            <div className="flex flex-wrap gap-2">
              {existingPhotos.map(photo => (
                <div key={photo.id} className="relative inline-block">
                  <img src={photo.url} alt="Foto do recebimento" className="w-24 h-24 object-cover rounded-lg border" />
                  <button
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs disabled:opacity-50"
                    onClick={() => handleDeleteExistingPhoto(photo.id)}
                    disabled={deletingPhotoId === photo.id}
                    title="Remover foto"
                  >
                    {deletingPhotoId === photo.id ? '…' : '×'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {newPhotos.length > 0 && (
          <div className="mt-3">
            <p className="text-xs text-slate-500 mb-2">Novas fotos ({newPhotos.length}) — sobem ao salvar o recebimento:</p>
            <div className="flex flex-wrap gap-2">
              {newPhotos.map((photo, idx) => (
                <div key={idx} className="relative inline-block">
                  <img src={photo.preview} alt={`Preview ${idx + 1}`} className="w-24 h-24 object-cover rounded-lg border" />
                  <button
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                    onClick={() => removeNewPhoto(idx)}
                    title="Remover foto"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => onConfirm(newPhotos.map(p => p.file))} className="bg-pink-600 hover:bg-pink-700 text-white">
            Confirmar
          </Button>
        </div>
      </div>
      <CameraCapture
        isOpen={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={async file => {
          await addPhotoFile(file);
        }}
      />
    </Modal>
  );
};

// ─── Split Item Modal ────────────────────────────────────────────

interface SplitModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: MaterialReceiptItemRow | null;
  onConfirm: (splitQty: number) => void;
}

const SplitModal: React.FC<SplitModalProps> = ({ isOpen, onClose, item, onConfirm }) => {
  const [splitQty, setSplitQty] = useState<number>(1);

  useEffect(() => {
    if (!isOpen) setSplitQty(1);
  }, [isOpen]);

  if (!item) return null;

  const maxSplit = item.quantity - 1;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Desmembrar Item</h3>
        <p className="text-sm text-slate-600 mb-4">
          <span className="font-medium">{item.description}</span> — Quantidade atual: <span className="font-bold">{item.quantity}</span>
        </p>

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Quantidade para o novo item
          </label>
          <Input
            type="number"
            min={1}
            max={maxSplit}
            step="0.01"
            value={splitQty}
            onChange={e => setSplitQty(parseFloat(e.target.value) || 1)}
          />
          <p className="text-xs text-slate-500 mt-1">
            Item original ficará com <span className="font-medium">{(item.quantity - splitQty).toFixed(2)}</span> unidades.
            Novo item terá <span className="font-medium">{splitQty}</span> unidades.
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => { onConfirm(splitQty); onClose(); }} disabled={splitQty < 1 || splitQty >= item.quantity}>
            Desmembrar
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ─── PO Lookup Modal (for individual item) ───────────────────────

interface POLookupModalProps {
  isOpen: boolean;
  onClose: () => void;
  providerId: number;
  item: MaterialReceiptItemRow | null;
  onSelect: (purchaseOrderId: number) => void;
}

const POLookupModal: React.FC<POLookupModalProps> = ({ isOpen, onClose, providerId, item, onSelect }) => {
  const [loading, setLoading] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrderForReceipt[]>([]);

  useEffect(() => {
    if (!isOpen || !providerId || !item) return;
    (async () => {
      setLoading(true);
      try {
        const all = await materialReceiptService.getPurchaseOrdersByProvider(providerId);
        // Filter only POs that contain this specific product/raw material
        const filtered = all.filter(po =>
          po.items.some(poItem =>
            (item.product_id && poItem.product_id === item.product_id) ||
            (item.raw_material_id && poItem.raw_material_id === item.raw_material_id),
          ),
        );
        setPurchaseOrders(filtered);
      } catch { setPurchaseOrders([]); }
      finally { setLoading(false); }
    })();
  }, [isOpen, providerId, item]);

  if (!item) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Vincular Pedido de Compra</h3>
        <p className="text-sm text-slate-500 mb-4">
          Selecione o pedido de compra que contém o item <span className="font-medium">"{item.description}"</span>.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin text-emerald-600" size={24} />
          </div>
        ) : purchaseOrders.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm">
            Nenhum pedido de compra encontrado para este fornecedor com este item.
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg divide-y">
            {purchaseOrders.map(po => (
              <button
                key={po.purchase_order_id}
                className="w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors"
                onClick={() => { onSelect(po.purchase_order_id); onClose(); }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-slate-900">Pedido #{po.order_number || po.purchase_order_id}</span>
                    <span className="block text-xs text-slate-500">
                      Data: {formatDate(po.order_date)} • Total: {formatCurrency(po.total)}
                    </span>
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${po.status === 0 ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                    {po.status === 0 ? 'Em Aberto' : 'Em Andamento'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </div>
      </div>
    </Modal>
  );
};

// ─── Sales Order Lookup Modal (for individual item) ──────────────

interface SalesOrderLookupModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: number;
  item: MaterialReceiptItemRow | null;
  onSelect: (orderId: number) => void;
}

const SalesOrderLookupModal: React.FC<SalesOrderLookupModalProps> = ({ isOpen, onClose, customerId, item, onSelect }) => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<CustomerOrderForReceipt[]>([]);

  useEffect(() => {
    if (!isOpen || !customerId || !item || !item.product_id) return;
    (async () => {
      setLoading(true);
      try {
        const result = await materialReceiptService.getCustomerOrdersByProduct(customerId, item.product_id!);
        setOrders(result);
      } catch { setOrders([]); }
      finally { setLoading(false); }
    })();
  }, [isOpen, customerId, item]);

  if (!item) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Vincular Pedido de Venda</h3>
        <p className="text-sm text-slate-500 mb-4">
          Selecione o pedido de venda que contém o item <span className="font-medium">"{item.description}"</span> com quantidade disponível para devolução.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin text-emerald-600" size={24} />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm">
            Nenhum pedido de venda encontrado para este cliente com este produto disponível para devolução.
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg divide-y">
            {orders.map(order => {
              const matchingItem = order.items?.find(i => i.product_id === item.product_id);
              const availableQty = matchingItem ? matchingItem.quantity - (matchingItem.returned_quantity ?? 0) : 0;
              return (
                <button
                  key={order.order_id}
                  className="w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors"
                  onClick={() => { onSelect(order.order_id); onClose(); }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-slate-900">Pedido #{order.order_id}</span>
                      <span className="block text-xs text-slate-500">
                        Data: {formatDate(order.order_date)} • Total: {formatCurrency(order.order_sell_total)}
                      </span>
                      {matchingItem && (
                        <span className="block text-xs text-emerald-600 mt-0.5">
                          {item._sourceCode || item.description} — Disponível: {availableQty} de {matchingItem.quantity}
                        </span>
                      )}
                    </div>
                    {order.order_status && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                        {order.order_status}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </div>
      </div>
    </Modal>
  );
};

// ─── Assign Purchase Orders Modal ────────────────────────────────

interface AssignPOModalProps {
  isOpen: boolean;
  onClose: () => void;
  receiptId: number;
  items: MaterialReceiptItemRow[];
  providerId: number;
  onAssign: (assignments: { tempId: string; purchaseOrderId: number | null }[]) => void;
  onSplitItem: (tempId: string, splitQty: number) => void;
}

const AssignPOModal: React.FC<AssignPOModalProps> = ({ isOpen, onClose, receiptId: _receiptId, items, providerId, onAssign, onSplitItem }) => {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<{
    item_id: string;
    description: string;
    code: string;
    quantity: number;
    suggestedPOs: { purchase_order_id: number; order_number?: number; order_date?: string; item_quantity: number }[];
  }[]>([]);
  const [selections, setSelections] = useState<Record<string, number | null>>({});
  const [splitItem, setSplitItem] = useState<{ tempId: string; quantity: number } | null>(null);
  const [splitQty, setSplitQty] = useState<number>(1);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      setLoading(true);
      try {
        // Get purchase orders for this provider
        const pos = await materialReceiptService.getPurchaseOrdersByProvider(providerId);
        // Build suggestions for each item
        const sugs = items.map(item => {
          const matchingPOs = pos.filter(po =>
            po.items.some(poItem =>
              (item.product_id && poItem.product_id === item.product_id) ||
              (item.raw_material_id && poItem.raw_material_id === item.raw_material_id),
            ),
          );
          return {
            item_id: item._tempId,
            description: item.description,
            code: item._sourceCode || '—',
            quantity: item.quantity,
            suggestedPOs: matchingPOs.map(po => ({
              purchase_order_id: po.purchase_order_id,
              order_number: po.order_number,
              order_date: po.order_date,
              item_quantity: po.items.find(pi =>
                (item.product_id && pi.product_id === item.product_id) ||
                (item.raw_material_id && pi.raw_material_id === item.raw_material_id),
              )?.quantity ?? 0,
            })),
          };
        });
        setSuggestions(sugs);
        // Initialize selections with existing purchase_order_id
        const init: Record<string, number | null> = {};
        items.forEach(item => {
          init[item._tempId] = item.purchase_order_id ?? null;
        });
        setSelections(init);
      } catch { setSuggestions([]); }
      finally { setLoading(false); }
    })();
  }, [isOpen, items, providerId]);

  const handleConfirm = () => {
    const assignments = Object.entries(selections).map(([tempId, poId]) => ({
      tempId,
      purchaseOrderId: poId,
    }));
    onAssign(assignments);
    onClose();
  };

  const handleSplit = () => {
    if (splitItem && splitQty > 0) {
      onSplitItem(splitItem.tempId, splitQty);
      setSplitItem(null);
      setSplitQty(1);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <LinkIcon size={20} className="text-emerald-600" />
          <h3 className="text-lg font-semibold text-slate-900">Atribuir Pedidos de Compra</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Associe cada item do recebimento ao pedido de compra correspondente. Use o desmembramento quando um item pertence a múltiplos pedidos.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-emerald-600" size={32} />
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b border-slate-200 text-xs text-slate-500 uppercase">
                  <th className="py-2 px-3 text-left">Código</th>
                  <th className="py-2 px-3 text-left">Descrição</th>
                  <th className="py-2 px-3 text-right">Qtde</th>
                  <th className="py-2 px-3 text-left">Pedido de Compra</th>
                  <th className="py-2 px-3 w-20">Ações</th>
                </tr>
              </thead>
              <tbody>
                {suggestions.map(sug => (
                  <tr key={sug.item_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3 text-xs text-slate-600">{sug.code}</td>
                    <td className="py-2 px-3 text-slate-900">{sug.description}</td>
                    <td className="py-2 px-3 text-right">{sug.quantity}</td>
                    <td className="py-2 px-3">
                      {sug.suggestedPOs.length === 0 ? (
                        <span className="text-xs text-slate-400 italic">Sem sugestões</span>
                      ) : (
                        <div className="space-y-1">
                          <label className="flex items-center gap-2 text-xs cursor-pointer">
                            <input
                              type="radio"
                              name={`po-${sug.item_id}`}
                              checked={selections[sug.item_id] === null}
                              onChange={() => setSelections(prev => ({ ...prev, [sug.item_id]: null }))}
                              className="text-slate-400"
                            />
                            <span className="text-slate-500">Não atribuir</span>
                          </label>
                          {sug.suggestedPOs.map(po => (
                            <label key={po.purchase_order_id} className="flex items-center gap-2 text-xs cursor-pointer">
                              <input
                                type="radio"
                                name={`po-${sug.item_id}`}
                                checked={selections[sug.item_id] === po.purchase_order_id}
                                onChange={() => setSelections(prev => ({ ...prev, [sug.item_id]: po.purchase_order_id }))}
                                className="text-emerald-600"
                              />
                              <span>
                                #{po.order_number || po.purchase_order_id} — {formatDate(po.order_date)} (Qtde PC: {po.item_quantity})
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-3 text-center">
                      {sug.quantity > 1 && (
                        <button
                          className="p-1 text-slate-400 hover:text-blue-600 transition-colors"
                          title="Desmembrar item"
                          onClick={() => setSplitItem({ tempId: sug.item_id, quantity: sug.quantity })}
                        >
                          <Scissors size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Inline split */}
        {splitItem && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-slate-900 mb-2">
              Desmembrar item (Qtde atual: {splitItem.quantity})
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min={1}
                max={splitItem.quantity - 1}
                value={splitQty}
                onChange={e => setSplitQty(parseFloat(e.target.value) || 1)}
                className="w-28"
              />
              <Button size="sm" onClick={handleSplit} disabled={splitQty < 1 || splitQty >= splitItem.quantity}>
                Confirmar
              </Button>
              <Button variant="outline" size="sm" onClick={() => setSplitItem(null)}>
                Cancelar
              </Button>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleConfirm}>Confirmar Atribuições</Button>
        </div>
      </div>
    </Modal>
  );
};

// ─── Main Component ──────────────────────────────────────────────

export const MaterialReceiptFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const { checkPermission } = usePermissions();
  const canViewFinancials = checkPermission('material_receipts.view_financials').hasPermission;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [receiptStatus, setReceiptStatus] = useState<number | null>(null);

  // Lookups
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  // Cartões de todas as empresas (o recebimento não tem empresa própria): parcela
  // paga com cartão provisiona na fatura do cartão escolhido.
  const [creditCards, setCreditCards] = useState<PurchaseCreditCard[]>([]);

  // Provider label
  const [providerLabel, setProviderLabel] = useState('');

  // Modal states
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [rawMaterialModalOpen, setRawMaterialModalOpen] = useState(false);
  const [poSelectionModalOpen, setPOSelectionModalOpen] = useState(false);
  const [assignPOModalOpen, setAssignPOModalOpen] = useState(false);

  // Damage modal
  const [damageModalOpen, setDamageModalOpen] = useState(false);
  const [damageTarget, setDamageTarget] = useState<MaterialReceiptItemRow | null>(null);
  const [damageModalMode, setDamageModalMode] = useState<'create' | 'edit'>('create');

  // Split modal
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const [splitTarget, setSplitTarget] = useState<MaterialReceiptItemRow | null>(null);

  // PO Lookup for individual item
  const [poLookupOpen, setPOLookupOpen] = useState(false);
  const [poLookupTarget, setPOLookupTarget] = useState<MaterialReceiptItemRow | null>(null);

  // Sales Order Lookup for individual item
  const [soLookupOpen, setSOLookupOpen] = useState(false);
  const [soLookupTarget, setSOLookupTarget] = useState<MaterialReceiptItemRow | null>(null);

  // Customer selection (for devoluções)
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [customerLabel, setCustomerLabel] = useState('');
  const [customerOrdersModalOpen, setCustomerOrdersModalOpen] = useState(false);
  const [customerOrders, setCustomerOrders] = useState<CustomerOrderForReceipt[]>([]);
  const [customerOrdersLoading, setCustomerOrdersLoading] = useState(false);
  const [expandedCustomerOrderId, setExpandedCustomerOrderId] = useState<number | null>(null);
  // Source mode: 'provider' or 'customer'
  const [sourceMode, setSourceMode] = useState<'provider' | 'customer'>('provider');

  // Installment generation
  const [installmentCount, setInstallmentCount] = useState<number | ''>('');

  // Form
  const [formData, setFormData] = useState<MaterialReceiptFormData>({
    receipt_date: new Date().toISOString().slice(0, 10),
    items: [],
    installments: [],
  });

  // ── Load lookups ──────────────────────────────────────────────

  useEffect(() => {
    (async () => {
      try {
        const [methods, cards] = await Promise.all([
          purchaseOrderService.getPaymentMethods().catch(() => []),
          purchaseOrderService.getPaymentCreditCards().catch(() => []),
        ]);
        setPaymentMethods(methods);
        setCreditCards(cards);
      } catch { /* ignore */ }
    })();
  }, []);

  // ── Load existing receipt ─────────────────────────────────────

  useEffect(() => {
    if (!isEditing || !id) return;
    (async () => {
      try {
        setLoading(true);
        const receipt = await materialReceiptService.getMaterialReceiptById(parseInt(id));
        setReceiptStatus(receipt.status ?? MATERIAL_RECEIPT_STATUS.IN_REVIEW);
        // Mapear itens e estabelecer relação pai-filho para avarias
        const allItems: MaterialReceiptItemRow[] = (receipt.items || []).map(item => ({
          ...item,
          _tempId: uid(),
          _type: (item.raw_material_id ? 'raw_material' : 'product') as 'product' | 'raw_material',
          _sourceCode: item.sku || '',
          _parentTempId: undefined,
        }));

        // Para itens avariados do backend, encontrar o item pai correspondente
        const nonDamagedItems = allItems.filter(i => !i.damaged);
        const damagedItems = allItems.filter(i => i.damaged);
        for (const dItem of damagedItems) {
          // Encontra o item pai: mesmo produto/matéria-prima, não avariado
          const parent = nonDamagedItems.find(p =>
            (dItem.product_id && p.product_id === dItem.product_id) ||
            (dItem.raw_material_id && p.raw_material_id === dItem.raw_material_id) ||
            (p.description === dItem.description),
          );
          if (parent) {
            dItem._parentTempId = parent._tempId;
          }
        }

        setFormData({
          receipt_date: receipt.receipt_date || '',
          provider_id: receipt.provider_id,
          customer_id: receipt.customer_id,
          notes: receipt.notes || '',
          internal_notes: receipt.internal_notes || '',
          carrier: receipt.carrier || '',
          volumes: receipt.volumes,
          items: allItems,
          installments: (receipt.installments || []).map(inst => ({
            ...inst,
            _tempId: uid(),
          })),
        });
        setProviderLabel(receipt.provider_name || '');
        if (receipt.customer_id) {
          setCustomerLabel(receipt.customer_name || `Cliente #${receipt.customer_id}`);
          setSourceMode('customer');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar recebimento');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEditing]);

  // ── Field helpers ─────────────────────────────────────────────

  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  // ── Provider search ───────────────────────────────────────────

  const searchProviders = useCallback(async (q: string): Promise<Provider[]> => {
    try {
      const res = await providerService.getProviders({
        page: 1, pageSize: 20, searchName: q,
      });
      return Array.isArray(res.data) ? res.data : [];
    } catch { return []; }
  }, []);

  const handleProviderSelect = (provider: Provider) => {
    const previousProviderId = formData.provider_id;
    const hasProductItems = formData.items.some(i => i._type === 'product' && i.purchase_order_id);

    updateField('provider_id', provider.provider_id);
    setProviderLabel(provider.trade_name || provider.legal_name);

    if (previousProviderId && previousProviderId !== provider.provider_id && hasProductItems) {
      setFormData(prev => ({
        ...prev,
        provider_id: provider.provider_id,
        items: prev.items.filter(i => !i.purchase_order_id),
      }));
    }
    setSourceMode('provider');
    // Clear customer when provider is selected
    setCustomerLabel('');
    updateField('customer_id', undefined);
  };

  // ── Customer select (for devoluções) ──────────────────────────

  const handleCustomerSelect = (customer: Customer) => {
    updateField('customer_id', customer.customer_id);
    setCustomerLabel(customer.name || `Cliente #${customer.customer_id}`);
    setSourceMode('customer');
    // Clear provider when customer is selected
    setProviderLabel('');
    updateField('provider_id', undefined);
    setCustomerOrders([]);
  };

  const handleFetchCustomerOrders = async () => {
    if (!formData.customer_id) return;
    setCustomerOrdersLoading(true);
    try {
      const orders = await materialReceiptService.getCustomerOrders(formData.customer_id);
      setCustomerOrders(orders);
      setCustomerOrdersModalOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao buscar pedidos do cliente');
    } finally {
      setCustomerOrdersLoading(false);
    }
  };

  const handleAddCustomerOrderItems = (
    items: { sku: string; description?: string; quantity: number; unit_price?: number; product_id?: number; orderId: number }[],
  ) => {
    const newItems: MaterialReceiptItemRow[] = items.map(item => ({
      _tempId: uid(),
      _type: 'product' as const,
      _sourceCode: item.sku,
      _orderNum: item.orderId,
      description: item.description || item.sku,
      unit: 'UN',
      unit_value: item.unit_price ?? 0,
      quantity: item.quantity,
      product_id: item.product_id,
      order_id: item.orderId,
    }));
    setFormData(prev => ({ ...prev, items: [...prev.items, ...newItems] }));
    setCustomerOrdersModalOpen(false);
  };

  // ── Product search (by provider) ──────────────────────────────

  const searchProducts = useCallback(async (q: string): Promise<ProviderProduct[]> => {
    if (!formData.provider_id) return [];
    try {
      return await materialReceiptService.searchProductsByProvider(formData.provider_id, q);
    } catch { return []; }
  }, [formData.provider_id]);

  const handleProductSelect = (product: ProviderProduct) => {
    const newItem: MaterialReceiptItemRow = {
      _tempId: uid(),
      _type: 'product',
      _sourceCode: product.sku,
      description: product.description,
      unit: product.unit || 'UN',
      unit_value: product.provider_cost,
      quantity: 1,
      product_id: product.product_id,
      raw_material_id: undefined,
      is_custom_product: product.is_custom_product === true,
    };
    setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
  };

  const openProductModal = () => {
    if (!formData.provider_id) {
      setErrors(prev => ({ ...prev, provider_id: 'Selecione um fornecedor antes de adicionar produtos' }));
      return;
    }
    setProductModalOpen(true);
  };

  // ── Raw material search ───────────────────────────────────────

  const searchRawMaterials = useCallback(async (q: string): Promise<RawMaterial[]> => {
    try {
      const res = await rawMaterialService.searchRawMaterials(q, 1, 20, { is_active: true });
      return res.rawMaterials || [];
    } catch { return []; }
  }, []);

  const handleRawMaterialSelect = (rm: RawMaterial) => {
    const newItem: MaterialReceiptItemRow = {
      _tempId: uid(),
      _type: 'raw_material',
      _sourceCode: rm.code,
      description: rm.description,
      unit: rm.unit || 'UN',
      unit_value: rm.cost || 0,
      quantity: 1,
      product_id: undefined,
      raw_material_id: rm.id,
    };
    setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
  };

  // ── Purchase Order selection ───────────────────────────────────

  const openPOSelectionModal = () => {
    if (!formData.provider_id) {
      setErrors(prev => ({ ...prev, provider_id: 'Selecione um fornecedor antes de adicionar itens de pedido de compra' }));
      return;
    }
    setPOSelectionModalOpen(true);
  };

  const handlePOItemsAdd = (newItems: MaterialReceiptItemRow[]) => {
    setFormData(prev => ({ ...prev, items: [...prev.items, ...newItems] }));
  };

  // ── Items CRUD ────────────────────────────────────────────────

  const updateItem = (tempId: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(i => i._tempId === tempId ? { ...i, [field]: value } : i),
    }));
  };

  const removeItem = (tempId: string) => {
    setFormData(prev => ({ ...prev, items: prev.items.filter(i => i._tempId !== tempId) }));
  };

  // ── Damage handling ───────────────────────────────────────────

  const openDamageModal = (item: MaterialReceiptItemRow) => {
    setDamageTarget(item);
    setDamageModalMode('create');
    setDamageModalOpen(true);
  };

  // ── Fotos do recebimento (produto personalizado) ──────────────
  const [receivedPhotosModalOpen, setReceivedPhotosModalOpen] = useState(false);
  const [receivedPhotosItem, setReceivedPhotosItem] = useState<MaterialReceiptItemRow | null>(null);

  const openReceivedPhotosModal = (item: MaterialReceiptItemRow) => {
    setReceivedPhotosItem(item);
    setReceivedPhotosModalOpen(true);
  };

  const handleReceivedPhotosConfirm = (photos: File[]) => {
    if (receivedPhotosItem) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.map(i =>
          i._tempId === receivedPhotosItem._tempId ? { ...i, _receivedPhotoFiles: photos } : i,
        ),
      }));
    }
    setReceivedPhotosModalOpen(false);
    setReceivedPhotosItem(null);
  };

  const openEditDamageModal = (item: MaterialReceiptItemRow) => {
    setDamageTarget(item);
    setDamageModalMode('edit');
    setDamageModalOpen(true);
  };

  const handleDamageConfirm = (damageNotes: string, damageQuantity: number, damagePhotos: File[]) => {
    if (!damageTarget) return;

    if (damageModalMode === 'edit') {
      // Atualiza notas, quantidade e fotos pendentes do item já marcado como avariado
      setFormData(prev => ({
        ...prev,
        items: prev.items.map(i =>
          i._tempId === damageTarget._tempId
            ? {
                ...i,
                damage_notes: damageNotes,
                quantity: damageQuantity,
                _damagePhotoFiles: damagePhotos.length > 0 ? [...(i._damagePhotoFiles || []), ...damagePhotos] : i._damagePhotoFiles,
              }
            : i,
        ),
      }));
      return;
    }

    // Modo criação: reduz quantidade do item original e cria novo item de avaria
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items.map(i =>
          i._tempId === damageTarget._tempId
            ? { ...i, quantity: i.quantity - damageQuantity }
            : i,
        ),
        {
          ...damageTarget,
          _tempId: uid(),
          material_receipt_item_id: undefined,
          quantity: damageQuantity,
          damaged: true,
          damage_notes: damageNotes,
          _damagePhotoFiles: damagePhotos.length > 0 ? damagePhotos : undefined,
          _parentTempId: damageTarget._tempId,
        },
      ],
    }));
  };

  const handleRemoveDamage = async (item: MaterialReceiptItemRow) => {
    // Se o item de avaria já está persistido no backend, chama API para removê-lo
    if (isEditing && id && item.material_receipt_item_id) {
      try {
        await materialReceiptService.removeDamage(parseInt(id), item.material_receipt_item_id);
      } catch (err) {
        console.error('Erro ao remover avaria no backend:', err);
        // Continua removendo do estado local mesmo se a API falhar
      }
    }
    // Remove o item de avaria e devolve a quantidade ao item pai
    setFormData(prev => {
      return {
        ...prev,
        items: prev.items
          .map(i => {
            if (i._tempId === item._parentTempId) {
              return { ...i, quantity: i.quantity + item.quantity };
            }
            return i;
          })
          .filter(i => i._tempId !== item._tempId),
      };
    });
  };

  // ── Split item handling ───────────────────────────────────────

  const _openSplitModal = (item: MaterialReceiptItemRow) => {
    setSplitTarget(item);
    setSplitModalOpen(true);
  };
  // Keep reference to suppress unused warning
  void _openSplitModal;

  const handleSplitConfirm = (splitQty: number) => {
    if (!splitTarget) return;
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items.map(i =>
          i._tempId === splitTarget._tempId
            ? { ...i, quantity: i.quantity - splitQty }
            : i,
        ),
        {
          ...splitTarget,
          _tempId: uid(),
          material_receipt_item_id: undefined,
          quantity: splitQty,
        },
      ],
    }));
  };

  // Handle split from Assign PO modal
  const handleSplitFromAssign = (tempId: string, splitQty: number) => {
    const item = formData.items.find(i => i._tempId === tempId);
    if (!item || splitQty >= item.quantity) return;
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items.map(i =>
          i._tempId === tempId ? { ...i, quantity: i.quantity - splitQty } : i,
        ),
        {
          ...item,
          _tempId: uid(),
          material_receipt_item_id: undefined,
          quantity: splitQty,
          purchase_order_id: undefined,
        },
      ],
    }));
  };

  // ── PO Lookup for item ────────────────────────────────────────

  const openPOLookup = (item: MaterialReceiptItemRow) => {
    setPOLookupTarget(item);
    setPOLookupOpen(true);
  };

  const handlePOLookupSelect = (purchaseOrderId: number) => {
    if (!poLookupTarget) return;
    updateItem(poLookupTarget._tempId, 'purchase_order_id', purchaseOrderId);
  };

  // ── Sales Order Lookup for item ───────────────────────────────

  const openSOLookup = (item: MaterialReceiptItemRow) => {
    setSOLookupTarget(item);
    setSOLookupOpen(true);
  };

  const handleSOLookupSelect = (orderId: number) => {
    if (!soLookupTarget) return;
    updateItem(soLookupTarget._tempId, 'order_id', orderId);
    // Also store for display
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(i => i._tempId === soLookupTarget._tempId ? { ...i, order_id: orderId, _orderNum: orderId } : i),
    }));
  };

  // ── Assign PO handling ────────────────────────────────────────

  const handleAssignPO = (assignments: { tempId: string; purchaseOrderId: number | null }[]) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(i => {
        const assignment = assignments.find(a => a.tempId === i._tempId);
        if (assignment) {
          return { ...i, purchase_order_id: assignment.purchaseOrderId ?? undefined };
        }
        return i;
      }),
    }));
  };

  // ── Installments CRUD ─────────────────────────────────────────

  const addInstallment = () => {
    const inst: MaterialReceiptInstallmentRow = {
      _tempId: uid(),
      value: 0,
      due_date: '',
      notes: '',
      payment_method_id: paymentMethods[0]?.payment_method_id,
    };
    setFormData(prev => ({ ...prev, installments: [...prev.installments, inst] }));
  };

  const isCardMethod = (paymentMethodId: number | undefined) =>
    isCreditCardMethodName(paymentMethods.find(m => m.payment_method_id === paymentMethodId)?.payment_method_name);

  const updateInstallment = (tempId: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      installments: prev.installments.map(i => {
        if (i._tempId !== tempId) return i;
        const next = { ...i, [field]: value };
        // Cartão só existe em forma de pagamento de cartão de crédito.
        if (field === 'payment_method_id' && !isCardMethod(value)) next.credit_card_id = null;
        return next;
      }),
    }));
  };

  const removeInstallment = (tempId: string) => {
    setFormData(prev => ({ ...prev, installments: prev.installments.filter(i => i._tempId !== tempId) }));
  };

  // ── Generate installments ─────────────────────────────────────

  const generateInstallments = () => {
    const count = typeof installmentCount === 'number' ? installmentCount : 0;
    if (count < 1 || count > 120) return;

    const total = totalItems;
    const baseValue = Math.floor((total / count) * 100) / 100;
    const remainder = Math.round((total - baseValue * count) * 100) / 100;

    const today = new Date();
    const newInstallments: MaterialReceiptInstallmentRow[] = Array.from({ length: count }, (_, idx) => {
      const dueDate = new Date(today);
      dueDate.setMonth(dueDate.getMonth() + idx + 1);
      return {
        _tempId: uid(),
        value: idx === 0 ? baseValue + remainder : baseValue,
        due_date: dueDate.toISOString().slice(0, 10),
        notes: `${idx + 1}ª parcela`,
        payment_method_id: paymentMethods[0]?.payment_method_id,
      };
    });

    setFormData(prev => ({ ...prev, installments: newInstallments }));
  };

  // ── Calculated totals ─────────────────────────────────────────

  const totalItems = formData.items.reduce((s, i) => s + (i.unit_value || 0) * (i.quantity || 0), 0);
  const totalInstallments = formData.installments.reduce((s, i) => s + (i.value || 0), 0);

  // ── Validate ──────────────────────────────────────────────────

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!formData.provider_id && !formData.customer_id) e.provider_id = 'Selecione um fornecedor ou cliente';
    if (formData.items.length === 0) e.items = 'Adicione ao menos um item';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Save ──────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      setError(null);

      // 1. Persiste o recebimento (cria ou atualiza)
      let savedReceipt;
      if (isEditing && id) {
        savedReceipt = await materialReceiptService.updateMaterialReceipt(parseInt(id), formData);
      } else {
        savedReceipt = await materialReceiptService.createMaterialReceipt(formData);
      }

      // 2. Faz upload das fotos de avaria pendentes
      //    Cada item com _damagePhotoFiles precisa ter um material_receipt_item_id real.
      //    Para itens existentes (edição) a ID já está no item do formulário.
      //    Para itens novos (criação), faz matching por atributos do item.
      const savedItems = savedReceipt?.items ?? [];
      const receiptId = savedReceipt?.material_receipt_id;

      if (receiptId) {
        for (const formItem of formData.items) {
          const photos = formItem._damagePhotoFiles || (formItem._damagePhotoFile ? [formItem._damagePhotoFile] : []);
          if (photos.length === 0) continue;

          // Resolve o ID persistido: preferir ID já presente, senão buscar no retorno do backend
          let itemId = formItem.material_receipt_item_id;
          if (!itemId) {
            // Match inteligente por atributos em vez de índice
            const match = savedItems.find(s =>
              s.damaged === formItem.damaged &&
              s.description === formItem.description &&
              s.product_id === formItem.product_id &&
              s.raw_material_id === formItem.raw_material_id &&
              Math.abs((s.quantity || 0) - (formItem.quantity || 0)) < 0.01,
            );
            itemId = match?.material_receipt_item_id;
          }
          if (!itemId) {
            console.warn('Não foi possível encontrar ID do item para upload de foto:', formItem.description);
            continue;
          }

          for (const photo of photos) {
            try {
              // Tenta endpoint de múltiplas fotos
              await materialReceiptService.uploadDamagePhoto(receiptId, itemId, photo);
            } catch {
              // Fallback: endpoint legado (suporta apenas 1 foto por item)
              try {
                await materialReceiptService.reportDamage(
                  receiptId, itemId, formItem.damage_notes || '', photo,
                );
              } catch (legacyErr) {
                console.error('Erro ao enviar foto de avaria (fallback):', legacyErr);
              }
              break; // Legacy suporta apenas 1 foto, interrompe o loop
            }
          }
        }
      }

      // 3. Faz upload das fotos do recebimento (produto personalizado) pendentes
      //    — mesmo matching por atributos usado nas fotos de avaria.
      if (receiptId) {
        for (const formItem of formData.items) {
          const receivedPhotos = formItem._receivedPhotoFiles ?? [];
          if (receivedPhotos.length === 0) continue;

          let itemId = formItem.material_receipt_item_id;
          if (!itemId) {
            const match = savedItems.find(s =>
              !s.damaged === !formItem.damaged &&
              s.description === formItem.description &&
              s.product_id === formItem.product_id &&
              s.raw_material_id === formItem.raw_material_id &&
              Math.abs((s.quantity || 0) - (formItem.quantity || 0)) < 0.01,
            );
            itemId = match?.material_receipt_item_id;
          }
          if (!itemId) {
            console.warn('Não foi possível encontrar ID do item para upload de foto do recebimento:', formItem.description);
            continue;
          }

          for (const photo of receivedPhotos) {
            try {
              await materialReceiptService.uploadReceivedPhoto(receiptId, itemId, photo);
            } catch (uploadErr) {
              console.error('Erro ao enviar foto do recebimento:', uploadErr);
            }
          }
        }
      }

      navigate('/material-receipts');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar recebimento');
    } finally {
      setSaving(false);
    }
  };

  // ── Loading skeleton ──────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/material-receipts')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
            <div className="h-4 bg-slate-200 rounded w-32 mt-2 animate-pulse" />
          </div>
        </div>
        {[...Array(4)].map((_, i) => (
          <Card key={i}><CardContent><div className="h-20 bg-slate-200 rounded animate-pulse" /></CardContent></Card>
        ))}
      </div>
    );
  }

  // Block editing a confirmed receipt
  if (isEditing && receiptStatus === MATERIAL_RECEIPT_STATUS.CONFIRMED) {
    return (
      <div className="space-y-6 max-w-5xl">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/material-receipts')}>
            <ArrowLeft size={20} />
          </Button>
          <h1 className="text-2xl font-bold text-slate-900">Editar Recebimento</h1>
        </div>
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-5 flex items-start gap-3">
          <AlertCircle size={20} className="text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-amber-800">Recebimento já confirmado</p>
            <p className="text-sm text-amber-700 mt-1">
              Este recebimento possui status <strong>Conferido</strong> e não pode mais ser editado.
              O estoque dos itens já foi processado pelo sistema.
            </p>
            <Button
              className="mt-4"
              variant="outline"
              onClick={() => navigate(`/material-receipts/${id}`)}
            >
              Ver Detalhes
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/material-receipts')}>
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isEditing ? 'Editar Recebimento de Material' : 'Novo Recebimento de Material'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isEditing ? `Editando recebimento #${id}` : 'Preencha os dados do recebimento de material'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
      )}

      {/* ── 1. Dados Gerais ───────────────────────────────────── */}
      <Card>
        <CardHeader title="Dados Gerais" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Fornecedor */}
            <div className="lg:col-span-1">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Fornecedor {sourceMode === 'provider' && <span className="text-red-500">*</span>}
              </label>
              <div className="flex gap-2">
                <input
                  className={`input flex-1 ${errors.provider_id && sourceMode === 'provider' ? 'border-red-400' : ''}`}
                  readOnly
                  placeholder="Selecione um fornecedor..."
                  value={providerLabel}
                />
                <Button variant="outline" onClick={() => setProviderModalOpen(true)} title="Buscar fornecedor">
                  <Search size={18} />
                </Button>
              </div>
              {errors.provider_id && <p className="text-xs text-red-500 mt-1">{errors.provider_id}</p>}
            </div>

            {/* Cliente */}
            <div className="lg:col-span-1">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Cliente {sourceMode === 'customer' && <span className="text-red-500">*</span>}
              </label>
              <div className="flex gap-2">
                <input
                  className={`input flex-1 ${errors.provider_id && sourceMode === 'customer' ? 'border-red-400' : ''}`}
                  readOnly
                  placeholder="Selecione um cliente..."
                  value={customerLabel}
                />
                <Button variant="outline" onClick={() => setCustomerModalOpen(true)} title="Buscar cliente">
                  <User size={18} />
                </Button>
              </div>
              {sourceMode === 'customer' && formData.customer_id && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  icon={<ShoppingCart size={14} />}
                  loading={customerOrdersLoading}
                  onClick={handleFetchCustomerOrders}
                >
                  Buscar Pedidos do Cliente
                </Button>
              )}
            </div>

            {/* Data do Recebimento */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Data do Recebimento</label>
              <DateInputBR className="input w-full" value={formData.receipt_date || ''} onChange={v => updateField('receipt_date', v)} />
            </div>

            {/* Transportadora */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Transportadora</label>
              <Input value={formData.carrier || ''} onChange={e => updateField('carrier', e.target.value)} placeholder="Nome da transportadora" />
            </div>

            {/* Volumes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Volumes</label>
              <Input type="number" min={0} value={formData.volumes ?? ''} onChange={e => updateField('volumes', e.target.value ? parseInt(e.target.value) : undefined)} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── 2. Itens do Recebimento ──────────────────────────── */}
      <Card>
        <CardHeader title="Itens do Recebimento" action={
          <div className="flex flex-wrap gap-2">
            {sourceMode === 'provider' && (
              <>
                <Button variant="outline" size="sm" icon={<Package size={16} />} onClick={openProductModal}>
                  Adicionar Produto
                </Button>
                <Button variant="outline" size="sm" icon={<Hammer size={16} />} onClick={() => setRawMaterialModalOpen(true)}>
                  Adicionar Matéria-Prima
                </Button>
                <Button variant="outline" size="sm" icon={<ShoppingCart size={16} />} onClick={openPOSelectionModal}>
                  Adicionar de Pedido de Compra
                </Button>
                <PermissionGuard permission="material_receipts.write">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<LinkIcon size={16} />}
                    onClick={() => {
                      if (!formData.provider_id) {
                        setErrors(prev => ({ ...prev, provider_id: 'Selecione um fornecedor primeiro' }));
                        return;
                      }
                      setAssignPOModalOpen(true);
                    }}
                  >
                    Atribuir Pedidos
                  </Button>
                </PermissionGuard>
              </>
            )}
            {sourceMode === 'customer' && formData.customer_id && (
              <Button
                variant="outline"
                size="sm"
                icon={<ShoppingCart size={16} />}
                loading={customerOrdersLoading}
                onClick={handleFetchCustomerOrders}
              >
                Buscar Pedidos do Cliente
              </Button>
            )}
          </div>
        } />
        <CardContent>
          {!formData.provider_id && !formData.customer_id && (
            <div className="flex items-center gap-2 mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              <AlertCircle size={16} className="shrink-0" />
              Selecione um fornecedor ou cliente antes de adicionar itens.
            </div>
          )}
          {errors.items && <p className="text-xs text-red-500 mb-3">{errors.items}</p>}
          {formData.items.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              Nenhum item adicionado. Use os botões acima para adicionar produtos, matérias-primas ou itens de um pedido de compra.
            </div>
          ) : (
            <div className="overflow-x-auto relative">
              <table className="w-full text-sm border-separate border-spacing-0">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
                    <th className="hidden md:table-cell py-2 px-2 w-16">Tipo</th>
                    <th className="py-2 px-2 w-20 md:w-24 sticky left-0 bg-white z-10">SKU</th>
                    <th className="py-2 px-2 min-w-[120px] md:min-w-0">Descrição</th>
                    <th className="py-2 px-2 w-auto md:hidden" />
                    <th className="hidden md:table-cell py-2 px-2 w-20">Unidade</th>
                    {canViewFinancials && <th className="hidden md:table-cell py-2 px-2 w-28 text-right">Valor Unit.</th>}
                    <th className="py-2 px-2 w-20 text-right">Qtde OK</th>
                    <th className="py-2 px-2 w-20 text-right text-red-600">Qtde Avaria</th>
                    <th className="py-2 px-2 w-20 text-right">Total Receb.</th>
                    {canViewFinancials && <th className="hidden md:table-cell py-2 px-2 w-28 text-right">Subtotal</th>}
                    <th className="hidden md:table-cell py-2 px-2 w-28">Pedido</th>
                    <th className="hidden md:table-cell py-2 px-2 w-24" />
                  </tr>
                </thead>
                <tbody>
                  {formData.items.filter(i => !i.damaged).map(item => {
                    // Calcula a quantidade de avaria vinculada a este item
                    const damagedItems = formData.items.filter(i => i.damaged && i._parentTempId === item._tempId);
                    const damageQty = damagedItems.reduce((sum, i) => sum + i.quantity, 0);
                    const totalQty = item.quantity + damageQty;

                    return (
                    <tr key={item._tempId} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="hidden md:table-cell py-2 px-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${item._type === 'product' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                          {item._type === 'product' ? <Package size={10} /> : <Hammer size={10} />}
                          {item._type === 'product' ? 'Prod' : 'MP'}
                        </span>
                      </td>
                      <td className="py-2 px-2 sticky left-0 bg-white z-10">
                        <span className="text-xs text-slate-600 font-mono">
                          {item._sourceCode || '—'}
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        <span className="text-xs text-slate-800 block" title={item.description}>
                          {item.description}
                        </span>
                      </td>
                      {/* Ações mobile — 3ª coluna visível apenas em mobile */}
                      <td className="py-2 px-2 md:hidden">
                        <div className="flex items-center gap-1">
                          {item.is_custom_product && (
                            <button
                              className="relative p-1.5 text-pink-500 hover:text-pink-700 bg-pink-50 rounded transition-colors"
                              title="Fotos do produto recebido (personalizado)"
                              onClick={() => openReceivedPhotosModal(item)}
                            >
                              <Camera size={16} />
                              {(item._receivedPhotoFiles?.length ?? 0) > 0 && (
                                <span className="absolute -top-1 -right-1 bg-pink-600 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">
                                  {item._receivedPhotoFiles!.length}
                                </span>
                              )}
                            </button>
                          )}
                          <button
                            className="p-1.5 text-red-500 hover:text-red-700 bg-red-50 rounded transition-colors"
                            title="Registrar avaria"
                            onClick={() => openDamageModal(item)}
                          >
                            <AlertTriangle size={16} />
                          </button>
                          {damagedItems.map(dItem => (
                            <button
                              key={dItem._tempId}
                              className="p-1.5 text-amber-500 hover:text-amber-700 bg-amber-50 rounded transition-colors"
                              title={`Editar avaria (${dItem.quantity} un.)`}
                              onClick={() => openEditDamageModal(dItem)}
                            >
                              <Edit size={16} />
                            </button>
                          ))}
                          <button
                            className="p-1.5 text-slate-400 hover:text-red-500 bg-slate-50 rounded transition-colors"
                            title="Remover item"
                            onClick={() => removeItem(item._tempId)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                      <td className="hidden md:table-cell py-2 px-2">
                        <select
                          className="input w-full text-xs"
                          value={item.unit || 'UN'}
                          onChange={e => updateItem(item._tempId, 'unit', e.target.value)}
                        >
                          {UNIT_OPTIONS.map(u => (
                            <option key={u.value} value={u.value}>{u.value}</option>
                          ))}
                        </select>
                      </td>
                      {canViewFinancials && (
                        <td className="hidden md:table-cell py-2 px-2 text-right">
                          <input
                            className="input w-full text-xs text-right"
                            type="number"
                            step="0.01"
                            min={0}
                            value={item.unit_value}
                            onChange={e => updateItem(item._tempId, 'unit_value', parseFloat(e.target.value) || 0)}
                          />
                        </td>
                      )}
                      <td className="py-2 px-2 text-right">
                        <input
                          className="input w-full text-xs text-right"
                          type="number"
                          step="0.01"
                          min={0}
                          value={item.quantity}
                          onChange={e => updateItem(item._tempId, 'quantity', parseFloat(e.target.value) || 0)}
                        />
                      </td>
                      <td className="py-2 px-2 text-right">
                        {damageQty > 0 ? (
                          <span className="text-xs font-medium text-red-600">{damageQty.toFixed(2)}</span>
                        ) : (
                          <span className="text-xs text-slate-400">0</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right">
                        <span className="text-xs font-semibold text-slate-900">{totalQty.toFixed(2)}</span>
                      </td>
                      {canViewFinancials && (
                        <td className="hidden md:table-cell py-2 px-2 text-right font-medium text-slate-900 whitespace-nowrap">
                          {formatCurrency((item.unit_value || 0) * totalQty)}
                        </td>
                      )}
                      <td className="hidden md:table-cell py-2 px-2">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-slate-500">
                            {item.purchase_order_id
                              ? `PC #${item.purchase_order_number || item.purchase_order_id}`
                              : item.order_id
                                ? <span className="text-emerald-600">PV #{item._orderNum || item.order_id}</span>
                                : '—'}
                          </span>
                          {formData.customer_id && item.product_id && !item.purchase_order_id ? (
                            <button
                              className="p-0.5 text-slate-400 hover:text-emerald-600 transition-colors"
                              title="Vincular pedido de venda"
                              onClick={() => openSOLookup(item)}
                            >
                              <Search size={12} />
                            </button>
                          ) : formData.provider_id && !item.order_id ? (
                            <button
                              className="p-0.5 text-slate-400 hover:text-emerald-600 transition-colors"
                              title="Vincular pedido de compra"
                              onClick={() => openPOLookup(item)}
                            >
                              <Search size={12} />
                            </button>
                          ) : null}
                        </div>
                      </td>
                      <td className="hidden md:table-cell py-2 px-2">
                        <div className="flex items-center gap-0.5">
                          <button
                            className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                            title="Registrar avaria"
                            onClick={() => openDamageModal(item)}
                          >
                            <AlertTriangle size={14} />
                          </button>
                          {damagedItems.map(dItem => (
                            <button
                              key={dItem._tempId}
                              className="p-1 text-amber-400 hover:text-amber-600 transition-colors"
                              title={`Editar avaria (${dItem.quantity} un.)`}
                              onClick={() => openEditDamageModal(dItem)}
                            >
                              <Edit size={14} />
                            </button>
                          ))}
                          {damagedItems.map(dItem => (
                            <button
                              key={`rm-${dItem._tempId}`}
                              className="p-1 text-red-300 hover:text-red-600 transition-colors"
                              title="Remover avaria"
                              onClick={() => handleRemoveDamage(dItem)}
                            >
                              <RotateCcw size={12} />
                            </button>
                          ))}
                          <button
                            className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                            title="Remover item"
                            onClick={() => removeItem(item._tempId)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                  {/* Itens avariados órfãos (sem item pai — ex: carregados do backend sem correspondência) */}
                  {formData.items.filter(i => i.damaged && !i._parentTempId).map(item => (
                    <tr key={item._tempId} className="border-b border-slate-100 bg-red-50">
                      <td className="hidden md:table-cell py-2 px-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${item._type === 'product' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                          {item._type === 'product' ? <Package size={10} /> : <Hammer size={10} />}
                          {item._type === 'product' ? 'Prod' : 'MP'}
                        </span>
                      </td>
                      <td className="py-2 px-2 sticky left-0 bg-red-50 z-10">
                        <span className="text-xs text-slate-600 font-mono">{item._sourceCode || '—'}</span>
                      </td>
                      <td className="py-2 px-2">
                        <span className="text-xs text-slate-700">{item.description}</span>
                      </td>
                      {/* Ações mobile para órfãos */}
                      <td className="py-2 px-2 md:hidden">
                        <div className="flex items-center gap-1">
                          <span className="inline-flex items-center gap-0.5 text-xs text-red-500"><AlertTriangle size={10} /></span>
                          <button
                            className="p-1.5 text-amber-500 hover:text-amber-700 bg-amber-50 rounded transition-colors"
                            title="Editar avaria"
                            onClick={() => openEditDamageModal(item)}
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className="p-1.5 text-slate-400 hover:text-red-500 bg-slate-50 rounded transition-colors"
                            title="Remover item"
                            onClick={() => removeItem(item._tempId)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                      <td className="hidden md:table-cell py-2 px-2">
                        <span className="text-xs text-slate-600">{item.unit || 'UN'}</span>
                      </td>
                      {canViewFinancials && (
                        <td className="hidden md:table-cell py-2 px-2 text-right text-xs">{formatCurrency(item.unit_value)}</td>
                      )}
                      <td className="py-2 px-2 text-right text-xs text-slate-400">0</td>
                      <td className="py-2 px-2 text-right">
                        <span className="text-xs font-medium text-red-600">{item.quantity.toFixed(2)}</span>
                      </td>
                      <td className="py-2 px-2 text-right">
                        <span className="text-xs font-semibold text-slate-900">{item.quantity.toFixed(2)}</span>
                      </td>
                      {canViewFinancials && (
                        <td className="hidden md:table-cell py-2 px-2 text-right font-medium text-slate-900 whitespace-nowrap">
                          {formatCurrency((item.unit_value || 0) * item.quantity)}
                        </td>
                      )}
                      <td className="hidden md:table-cell py-2 px-2">
                        <span className="text-xs text-slate-500">
                          {item.purchase_order_id
                            ? `PC #${item.purchase_order_number || item.purchase_order_id}`
                            : item.order_id
                              ? <span className="text-emerald-600">PV #{item._orderNum || item.order_id}</span>
                              : '—'}
                        </span>
                      </td>
                      <td className="hidden md:table-cell py-2 px-2">
                        <div className="flex items-center gap-0.5">
                          <span className="inline-flex items-center gap-1 text-xs text-red-500"><AlertTriangle size={12} /> Avaria</span>
                          <button
                            className="p-1 text-amber-400 hover:text-amber-600 transition-colors"
                            title="Editar avaria"
                            onClick={() => openEditDamageModal(item)}
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                            title="Remover item"
                            onClick={() => removeItem(item._tempId)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  {(() => {
                    const nonDamagedItems = formData.items.filter(i => !i.damaged);
                    const totalQuantityAll = formData.items.reduce((s, i) => s + (i.quantity || 0), 0);
                    const totalDamageQty = formData.items.filter(i => i.damaged).reduce((s, i) => s + (i.quantity || 0), 0);
                    const totalOkQty = nonDamagedItems.reduce((s, i) => s + (i.quantity || 0), 0);
                    return (
                      <>
                        <tr className="border-t-2 border-slate-300">
                          <td colSpan={canViewFinancials ? 5 : 4} className="hidden md:table-cell py-2 px-2 text-right font-semibold text-slate-700">Totais:</td>
                          <td className="md:hidden py-2 px-2 text-right font-semibold text-slate-700" colSpan={3}>Totais:</td>
                          <td className="py-2 px-2 text-right font-bold text-slate-700">{totalOkQty.toFixed(2)}</td>
                          <td className="py-2 px-2 text-right font-bold text-red-600">{totalDamageQty.toFixed(2)}</td>
                          <td className="py-2 px-2 text-right font-bold text-slate-900">{totalQuantityAll.toFixed(2)}</td>
                          {canViewFinancials && (
                            <td className="hidden md:table-cell py-2 px-2 text-right font-bold text-emerald-700">{formatCurrency(totalItems)}</td>
                          )}
                          <td className="hidden md:table-cell" colSpan={2} />
                        </tr>
                      </>
                    );
                  })()}
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── 3. Parcelas ───────────────────────────────────────── */}
      {canViewFinancials && (
      <Card>
        <CardHeader title="Parcelas" action={
          <PermissionGuard permission="material_receipts.manage_installments">
            <Button variant="outline" size="sm" icon={<Plus size={16} />} onClick={addInstallment}>
              Adicionar Parcela
            </Button>
          </PermissionGuard>
        } />
        <CardContent>
          {/* Gerar parcelas automaticamente */}
          <PermissionGuard permission="material_receipts.manage_installments">
            <div className="flex items-end gap-3 mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Qtde de Parcelas</label>
                <input
                  type="number"
                  min={1}
                  max={120}
                  className="input w-28 text-sm"
                  placeholder="Ex: 4"
                  value={installmentCount}
                  onChange={e => setInstallmentCount(e.target.value ? parseInt(e.target.value) : '')}
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                icon={<Calculator size={16} />}
                onClick={generateInstallments}
                disabled={!installmentCount || installmentCount < 1}
              >
                Gerar Parcelas
              </Button>
              <span className="text-xs text-slate-500 pb-2">
                Total para distribuição: {formatCurrency(totalItems)}
              </span>
            </div>
          </PermissionGuard>
          {formData.installments.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              Nenhuma parcela adicionada. Clique em "Adicionar Parcela" para incluir.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
                    <th className="py-2 px-2 w-10">#</th>
                    <th className="py-2 px-2 w-36">Vencimento</th>
                    <th className="py-2 px-2 w-32 text-right">Valor (R$)</th>
                    <th className="py-2 px-2">Forma de Pagamento</th>
                    <th className="py-2 px-2">Observação</th>
                    <th className="py-2 px-2 w-12" />
                  </tr>
                </thead>
                <tbody>
                  {formData.installments.map((inst, idx) => (
                    <tr key={inst._tempId} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-2 text-slate-500 font-medium">{idx + 1}</td>
                      <td className="py-2 px-2">
                        <DateInputBR
                          className="input w-full text-xs"
                          value={inst.due_date || ''}
                          onChange={v => updateInstallment(inst._tempId, 'due_date', v)}
                        />
                      </td>
                      <td className="py-2 px-2 text-right">
                        <input
                          type="number"
                          step="0.01"
                          min={0}
                          className="input w-full text-xs text-right"
                          value={inst.value}
                          onChange={e => updateInstallment(inst._tempId, 'value', parseFloat(e.target.value) || 0)}
                        />
                      </td>
                      <td className="py-2 px-2">
                        <select
                          className="input w-full text-xs"
                          value={inst.payment_method_id || ''}
                          onChange={e => updateInstallment(inst._tempId, 'payment_method_id', e.target.value ? Number(e.target.value) : undefined)}
                        >
                          <option value="">Selecione...</option>
                          {paymentMethods.map(m => (
                            <option key={m.payment_method_id} value={m.payment_method_id}>{m.payment_method_name}</option>
                          ))}
                        </select>
                        {isCardMethod(inst.payment_method_id) && creditCards.length > 0 && (
                          <select
                            className="input w-full text-xs mt-1"
                            value={inst.credit_card_id || ''}
                            onChange={e => updateInstallment(inst._tempId, 'credit_card_id', e.target.value ? Number(e.target.value) : null)}
                            title="Cartão da empresa — a provisão vai para a fatura dele (sem cartão, vai para a conta corrente)"
                          >
                            <option value="">Cartão…</option>
                            {creditCards.map(c => (
                              <option key={c.credit_card_id} value={c.credit_card_id}>{c.description} · {c.company_name}</option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="py-2 px-2">
                        <input
                          className="input w-full text-xs"
                          value={inst.notes || ''}
                          onChange={e => updateInstallment(inst._tempId, 'notes', e.target.value)}
                          placeholder="Observação da parcela..."
                        />
                      </td>
                      <td className="py-2 px-2 text-center">
                        <PermissionGuard permission="material_receipts.manage_installments">
                          <button className="p-1 text-slate-400 hover:text-red-500 transition-colors" onClick={() => removeInstallment(inst._tempId)}>
                            <Trash2 size={16} />
                          </button>
                        </PermissionGuard>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300">
                    <td />
                    <td className="py-2 px-2 text-right font-semibold text-slate-700">Total Parcelas:</td>
                    <td className="py-2 px-2 text-right font-bold text-emerald-700">{formatCurrency(totalInstallments)}</td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* ── 4. Observações ────────────────────────────────────── */}
      <Card>
        <CardHeader title="Observações" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Observações</label>
              <textarea
                className="input w-full min-h-[80px] resize-y"
                placeholder="Observações do recebimento..."
                value={formData.notes || ''}
                onChange={e => updateField('notes', e.target.value)}
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Observações Internas</label>
              <textarea
                className="input w-full min-h-[80px] resize-y"
                placeholder="Uso interno, não será visível no recebimento..."
                value={formData.internal_notes || ''}
                onChange={e => updateField('internal_notes', e.target.value)}
                rows={3}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Actions ───────────────────────────────────────────── */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/material-receipts')} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={handleSave} disabled={saving} icon={<Save size={20} />}>
          {saving ? 'Salvando...' : isEditing ? 'Salvar Alterações' : 'Criar Recebimento'}
        </Button>
      </div>

      {/* ── Search Modals ─────────────────────────────────────── */}

      {/* Provider */}
      <SearchModal<Provider>
        isOpen={providerModalOpen}
        onClose={() => setProviderModalOpen(false)}
        title="Buscar Fornecedor"
        onSelect={handleProviderSelect}
        searchFn={searchProviders}
        keyFn={p => p.provider_id}
        renderItem={p => (
          <div>
            <span className="font-medium text-slate-900">{p.trade_name || p.legal_name}</span>
            <span className="block text-xs text-slate-500">{p.document} • {p.email || '—'}</span>
          </div>
        )}
      />

      {/* Product (by provider) */}
      <SearchModal<ProviderProduct>
        isOpen={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        title={`Buscar Produto — ${providerLabel || 'Fornecedor'}`}
        onSelect={handleProductSelect}
        searchFn={searchProducts}
        keyFn={p => p.product_id}
        renderItem={p => (
          <div className="flex items-center gap-3">
            <Package size={16} className="text-blue-500 shrink-0" />
            <div>
              <span className="font-medium text-slate-900">{p.description}</span>
              <span className="block text-xs text-slate-500">
                SKU: {p.sku}{p.supplier_code ? ` • Cód. Forn: ${p.supplier_code}` : ''} • Custo Fornecedor: {formatCurrency(p.provider_cost)} • Preço Venda: {formatCurrency(p.price)}
              </span>
            </div>
          </div>
        )}
      />

      {/* Raw Material */}
      <SearchModal<RawMaterial>
        isOpen={rawMaterialModalOpen}
        onClose={() => setRawMaterialModalOpen(false)}
        title="Buscar Matéria-Prima"
        onSelect={handleRawMaterialSelect}
        searchFn={searchRawMaterials}
        keyFn={r => r.id}
        renderItem={r => (
          <div className="flex items-center gap-3">
            <Hammer size={16} className="text-amber-500 shrink-0" />
            <div>
              <span className="font-medium text-slate-900">{r.description}</span>
              <span className="block text-xs text-slate-500">
                Código: {r.code} • Custo: {formatCurrency(r.cost)} • {r.unit || '—'}
              </span>
            </div>
          </div>
        )}
      />

      {/* Purchase Order Selection */}
      <PurchaseOrderSelectionModal
        isOpen={poSelectionModalOpen}
        onClose={() => setPOSelectionModalOpen(false)}
        providerId={formData.provider_id || 0}
        onAddItems={handlePOItemsAdd}
      />

      {/* Damage Modal */}
      <ReceivedPhotosModal
        isOpen={receivedPhotosModalOpen}
        onClose={() => { setReceivedPhotosModalOpen(false); setReceivedPhotosItem(null); }}
        item={receivedPhotosItem}
        receiptId={isEditing && id ? parseInt(id) : undefined}
        onConfirm={handleReceivedPhotosConfirm}
      />
      <DamageModal
        isOpen={damageModalOpen}
        onClose={() => { setDamageModalOpen(false); setDamageTarget(null); }}
        item={damageTarget}
        onConfirm={handleDamageConfirm}
        mode={damageModalMode}
        initialNotes={damageModalMode === 'edit' ? (damageTarget?.damage_notes ?? '') : ''}
        initialQuantity={damageModalMode === 'edit' ? (damageTarget?.quantity ?? 1) : 1}
        receiptId={id ? parseInt(id) : undefined}
        existingItemId={damageModalMode === 'edit' ? (damageTarget?.material_receipt_item_id ?? undefined) : undefined}
      />

      {/* Split Modal */}
      <SplitModal
        isOpen={splitModalOpen}
        onClose={() => { setSplitModalOpen(false); setSplitTarget(null); }}
        item={splitTarget}
        onConfirm={handleSplitConfirm}
      />

      {/* PO Lookup Modal (for individual item) */}
      <POLookupModal
        isOpen={poLookupOpen}
        onClose={() => { setPOLookupOpen(false); setPOLookupTarget(null); }}
        providerId={formData.provider_id || 0}
        item={poLookupTarget}
        onSelect={handlePOLookupSelect}
      />

      {/* Sales Order Lookup Modal (for individual item) */}
      <SalesOrderLookupModal
        isOpen={soLookupOpen}
        onClose={() => { setSOLookupOpen(false); setSOLookupTarget(null); }}
        customerId={formData.customer_id || 0}
        item={soLookupTarget}
        onSelect={handleSOLookupSelect}
      />

      {/* Assign Purchase Orders Modal */}
      <AssignPOModal
        isOpen={assignPOModalOpen}
        onClose={() => setAssignPOModalOpen(false)}
        receiptId={parseInt(id || '0')}
        items={formData.items}
        providerId={formData.provider_id || 0}
        onAssign={handleAssignPO}
        onSplitItem={handleSplitFromAssign}
      />

      {/* Customer Search Modal */}
      <CustomerSearchModal
        isOpen={customerModalOpen}
        onClose={() => setCustomerModalOpen(false)}
        onSelect={handleCustomerSelect}
      />

      {/* Customer Orders Modal */}
      <CustomerOrdersSelectionModal
        isOpen={customerOrdersModalOpen}
        onClose={() => setCustomerOrdersModalOpen(false)}
        orders={customerOrders}
        expandedOrderId={expandedCustomerOrderId}
        onToggleOrder={setExpandedCustomerOrderId}
        onAddItems={handleAddCustomerOrderItems}
      />
    </div>
  );
};
